import api from './api';

/** A lead_status_configs key. Was a three-value union while leads only ever
 *  meant a Clalit referral; the vocabulary is per tenant now. */
export type LeadStatus = string;

/** The three the IL flow ships with, still referred to by name where the
 *  referral ladder is concerned. */
export const IL_LEAD_STATUSES = {
  CREATED: 'CREATED',
  MATCHED: 'MATCHED',
  NOT_INTERESTED: 'NOT_INTERESTED',
} as const;

export interface Lead {
  id: number;
  tenant_id: number;
  sub_clinic: string | null;
  first_name: string;
  last_name: string;
  id_number: string | null;
  obligation_number: string | null;
  phone: string | null;
  email: string | null;
  clinic_id: number | null;
  status: LeadStatus;
  /** The tenant's own fields, keyed by lead_field_configs.key. */
  additional_fields: Record<string, any> | null;
  matched_at: string | null;
  questionnaire_sent_at: string | null;
  /** Set when a patient was created from this lead. */
  converted_at: string | null;
  referral_file_id: number | null;
  /** The referral SOURCE (insurer/partner) this lead came in through, captured
   *  from the ?referral=<hash> link. referral_name is resolved for display. */
  referral_id: number | null;
  referral_name: string | null;
  patient_id: number | null;
  patient_status: string | null;
  patient_status_at: string | null;
  patient_ever_seen: boolean | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface LeadListResponse {
  items: Lead[];
  total: number;
}

export interface LeadFilters {
  skip?: number;
  limit?: number;
  search?: string;
  search_field?: string;
  sub_clinic?: string;
  status?: LeadStatus | '';
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface LeadPayload {
  first_name: string;
  last_name: string;
  id_number?: string | null;
  phone?: string | null;
  email?: string | null;
  clinic_id?: number | null;
  status?: string | null;
  sub_clinic?: string | null;
  additional_fields?: Record<string, any> | null;
}

/** One entry of GET /leads/fields - the same shape GET /patients/fields returns. */
export interface LeadFieldOption {
  name: string;
  label: string;
  type: 'builtin' | 'dynamic';
  field_type?: string;
  options?: { value: string; label: string }[];
}

export const leadService = {
  /** Leads. Paged, sorted and searched on the server - a district can run to
   *  hundreds and the table only ever holds one page. */
  async getLeads(filters: LeadFilters = {}): Promise<LeadListResponse> {
    const params = new URLSearchParams();

    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.search_field) params.append('search_field', filters.search_field);
    if (filters.sub_clinic) params.append('sub_clinic', filters.sub_clinic);
    if (filters.status) params.append('status', filters.status);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/leads/?${params}`);
    return response.data;
  },

  async getLead(leadId: number): Promise<Lead> {
    const response = await api.get(`/leads/${leadId}`);
    return response.data;
  },

  async createLead(payload: LeadPayload): Promise<Lead> {
    const response = await api.post('/leads/', payload);
    // The create response wraps the lead alongside non-blocking warnings.
    return response.data.lead;
  },

  async updateLead(leadId: number, payload: Partial<LeadPayload>): Promise<Lead> {
    const response = await api.put(`/leads/${leadId}`, payload);
    return response.data;
  },

  /** Built-in and dynamic lead fields, for anything that needs to name one -
   *  the questionnaire builder's mapping list and the lead-to-patient map.
   *  Pass tenantId to read a specific tenant's fields (the mapping screen a
   *  system user drives for another tenant); omit it to use the caller's own. */
  async getLeadFields(tenantId?: number): Promise<LeadFieldOption[]> {
    const params = tenantId !== undefined ? `?tenant_id=${tenantId}` : '';
    const response = await api.get(`/leads/fields${params}`);
    return response.data;
  },

  async sendEmail(leadId: number, templateId: number): Promise<void> {
    await api.post(`/leads/${leadId}/send-email`, { template_id: templateId });
  },

  async sendSms(leadId: number, templateId: number, content?: string): Promise<void> {
    await api.post(`/leads/${leadId}/send-sms`, {
      template_id: templateId,
      // Only present when the sender edited the text; absent keeps the
      // server's own template rendering.
      ...(content !== undefined ? { content } : {}),
    });
  },

  async sendQuestionnaire(
    leadId: number,
    questionnaireTypeId: number,
    emailTemplateId: number,
  ): Promise<void> {
    await api.post(`/leads/${leadId}/send-questionnaire`, {
      questionnaire_type_id: questionnaireTypeId,
      email_template_id: emailTemplateId,
    });
  },
};

export default leadService;
